<template>
  <v-dialog :model-value="modelValue" @update:model-value="$emit('update:modelValue', $event)" max-width="960" persistent scrollable>
    <v-card>
      <v-card-title class="bg-primary d-flex align-center">
        <v-icon start>{{ editing ? 'mdi-pencil' : 'mdi-pencil-plus' }}</v-icon>
        {{ editing ? $t('blog.editPost') : $t('blog.writePost') }}
      </v-card-title>

      <v-card-text class="pa-6">
        <!-- Header image: upload or URL -->
        <div class="mb-4">
          <div class="d-flex align-center ga-2 mb-2">
            <span class="text-body-2 font-weight-medium">{{ $t('blog.headerImage') }}</span>
            <v-btn-toggle v-model="imageMode" mandatory density="compact" variant="outlined" size="small">
              <v-btn value="upload" size="small">
                <v-icon start size="16">mdi-upload</v-icon>
                {{ $t('blog.upload') }}
              </v-btn>
              <v-btn value="url" size="small">
                <v-icon start size="16">mdi-link</v-icon>
                {{ $t('blog.linkUrl') }}
              </v-btn>
            </v-btn-toggle>
          </div>

          <!-- Upload mode -->
          <div v-if="imageMode === 'upload'">
            <v-file-input
              v-model="headerFile"
              :label="$t('blog.chooseImage')"
              variant="outlined"
              accept="image/*"
              prepend-icon=""
              prepend-inner-icon="mdi-image"
              density="compact"
              hide-details
              @update:model-value="handleHeaderUpload"
            />
            <v-progress-linear v-if="uploading" indeterminate color="primary" class="mt-1" />
          </div>

          <!-- URL mode -->
          <v-text-field
            v-else
            v-model="form.headerImage"
            placeholder="https://example.com/image.jpg"
            variant="outlined"
            prepend-inner-icon="mdi-link"
            density="compact"
            hide-details
          />

          <!-- Preview -->
          <v-img
            v-if="form.headerImage"
            :src="form.headerImage"
            height="180"
            cover
            class="rounded mt-3"
          >
            <template #default>
              <div class="d-flex justify-end pa-2">
                <v-btn icon size="small" color="error" variant="flat" @click="removeHeaderImage">
                  <v-icon size="18">mdi-close</v-icon>
                </v-btn>
              </div>
            </template>
            <template #error>
              <v-alert type="warning" density="compact">{{ $t('blog.imageLoadError') }}</v-alert>
            </template>
          </v-img>
        </div>

        <!-- Category -->
        <v-select
          v-model="form.categoryId"
          :items="categories"
          :item-title="c => $t(`blog.categories.${c.slug}`)"
          item-value="categoryId"
          :label="$t('blog.category')"
          variant="outlined"
          class="mb-4"
          hide-details
          :rules="[v => !!v || $t('blog.categoryRequired')]"
        >
          <template #item="{ item, props: itemProps }">
            <v-list-item v-bind="itemProps">
              <template #prepend>
                <v-icon :color="item.raw.color">{{ item.raw.icon }}</v-icon>
              </template>
            </v-list-item>
          </template>
        </v-select>

        <!-- Title -->
        <v-text-field
          v-model="form.title"
          :label="$t('blog.postTitle')"
          variant="outlined"
          class="mb-4"
          counter="255"
          :rules="[v => (v && v.length >= 5) || $t('blog.titleMinLength')]"
        />

        <!-- Rich text editor -->
        <div class="editor-wrapper mb-2">
          <VuetifyTiptap
            v-model="form.body"
            :label="$t('blog.postBody')"
            rounded
            :min-height="300"
            :max-height="600"
            :extensions="extensions"
          />
        </div>

        <div class="text-caption text-grey mb-2">
          {{ bodyTextLength }} {{ $t('blog.characters') }}
          <span v-if="bodyTextLength < 50" class="text-error">
            — {{ $t('blog.minCharacters', { min: 50 }) }}
          </span>
        </div>
      </v-card-text>

      <v-card-actions class="pa-4">
        <v-spacer />
        <v-btn @click="$emit('update:modelValue', false)">{{ $t('common.cancel') }}</v-btn>
        <v-btn
          color="primary"
          :loading="saving"
          :disabled="!isValid"
          @click="save"
        >
          {{ editing ? $t('blog.update') : $t('blog.publish') }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup>
/* eslint-disable */
import { computed, reactive, ref, watch } from 'vue'
import { useStore } from 'vuex'
import {
  BaseKit, Bold, Italic, Underline, Strike,
  Color, Highlight, Heading, Link, Image,
  Video, Table, Fullscreen, History,
  BulletList, OrderedList, TextAlign, Blockquote,
  HorizontalRule, Clear, FontSize, SubAndSuperScript,
  VuetifyTiptap
} from 'vuetify-pro-tiptap'
import 'vuetify-pro-tiptap/style.css'
import api from '../../services/api'

const extensions = [
  BaseKit.configure({
    placeholder: { placeholder: 'Begynd at skrive dit indlæg...' },
    bubble: {
      list: {
        image: ['float-left', 'float-none', 'float-right', 'divider', 'size-small', 'size-medium', 'size-large', 'divider', 'textAlign', 'divider', 'image', 'image-aspect-ratio', 'remove'],
        text: ['bold', 'italic', 'underline', 'strike', 'divider', 'color', 'highlight', 'textAlign', 'divider', 'link'],
        video: ['video', 'remove']
      }
    }
  }),
  Bold,
  Italic,
  Underline,
  Strike,
  Heading.configure({
    levels: [2, 3, 4],
  }),
  FontSize,
  Color,
  Highlight,
  BulletList,
  OrderedList,
  TextAlign,
  Blockquote,
  HorizontalRule,
  Link,
  Image.configure({
    upload: handleEditorImageUpload
  }),
  Video,
  Table,
  SubAndSuperScript,
  Clear,
  Fullscreen.configure({ spacer: true }),
  History
]

const props = defineProps({
  modelValue: { type: Boolean, default: false },
  categories: { type: Array, default: () => [] },
  editing: { type: Object, default: null }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const store = useStore()
const saving = ref(false)
const uploading = ref(false)
const imageMode = ref('upload')
const headerFile = ref(null)

const form = reactive({
  title: '',
  body: '',
  categoryId: null,
  headerImage: ''
})

// Strip HTML to get plain text length for validation
const bodyTextLength = computed(() => {
  if (!form.body) return 0
  const tmp = document.createElement('div')
  tmp.innerHTML = form.body
  return (tmp.textContent || tmp.innerText || '').trim().length
})

const isValid = computed(() =>
  form.title.length >= 5 && bodyTextLength.value >= 50 && form.categoryId
)

// Populate form when editing
watch(() => props.editing, (post) => {
  if (post) {
    form.title = post.title || ''
    form.body = post.body || ''
    form.categoryId = post.categoryId || null
    form.headerImage = post.headerImage || ''
  }
}, { immediate: true })

// Reset form when dialog opens for new post
watch(() => props.modelValue, (open) => {
  if (open && !props.editing) {
    form.title = ''
    form.body = ''
    form.categoryId = null
    form.headerImage = ''
    headerFile.value = null
  }
})

// ── Header image upload ──────────────────────────────────────────────────

async function handleHeaderUpload(files) {
  const file = Array.isArray(files) ? files[0] : files
  if (!file) return

  uploading.value = true
  try {
    const url = await uploadImage(file)
    form.headerImage = url
  } catch (error) {
    console.error('Header image upload failed:', error)
  } finally {
    uploading.value = false
  }
}

function removeHeaderImage() {
  form.headerImage = ''
  headerFile.value = null
}

// ── Editor inline image upload ───────────────────────────────────────────

async function handleEditorImageUpload(file) {
  const url = await uploadImage(file)
  return url
}

// ── Shared upload helper ─────────────────────────────────────────────────

async function uploadImage(file) {
  const formData = new FormData()
  formData.append('image', file)

  const { data } = await api.post('/upload/image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  })

  return data.url
}

// ── Save ─────────────────────────────────────────────────────────────────

const save = async () => {
  if (!isValid.value || saving.value) return
  saving.value = true

  try {
    if (props.editing) {
      await store.dispatch('blog/updatePost', {
        postId: props.editing.postId,
        ...form
      })
      emit('saved', { postId: props.editing.postId, slug: props.editing.slug })
    } else {
      const result = await store.dispatch('blog/createPost', { ...form })
      emit('saved', result)
    }
  } catch (error) {
    console.error('Save post failed:', error)
  } finally {
    saving.value = false
  }
}
</script>

<style scoped>
.editor-wrapper {
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 8px;
  overflow: hidden;
}
.editor-wrapper :deep(.vuetify-pro-tiptap-editor__content) {
  min-height: 300px;
}
</style>